import React, { FunctionComponent, useMemo } from 'react'
import {
  Badge,
  Box,
  Button,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack
} from '@chakra-ui/react'
import { format, parseISO } from 'date-fns'
import { rentalApplicationFees } from '@homevest/utils'

import { ContentSectionCard } from 'components/TailwindUIToolkit/Display/Cards/ContentSectionCard'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import { CopyIcon } from 'components/TailwindUIToolkit/Utils'
import { REQUEST_POLICY } from 'constants/urql'
import {
  RentalApplicationFeesByUserIdQuery,
  useRentalApplicationFeesByUserIdQuery
} from 'graphql/generated'
import { formatMoney } from 'lib/numbers'
import { Link } from 'ui'

type ApplicationFee = RentalApplicationFeesByUserIdQuery['rental_application_fees'][number]

type Props = {
  userId: string
}

const {
  RENTAL_APPLICATION_FEE_CATEGORIES,
  RENTAL_APPLICATION_FEE_SELECTIONS,
  resolveRentalApplicationFees
} = rentalApplicationFees

type RentalApplicationFeeCategory =
  (typeof RENTAL_APPLICATION_FEE_CATEGORIES)[keyof typeof RENTAL_APPLICATION_FEE_CATEGORIES]
type RentalApplicationFeeSelection =
  (typeof RENTAL_APPLICATION_FEE_SELECTIONS)[keyof typeof RENTAL_APPLICATION_FEE_SELECTIONS]

const CATEGORY_LABELS: Record<RentalApplicationFeeCategory, string> = {
  [RENTAL_APPLICATION_FEE_CATEGORIES.PAID]: 'Paid',
  [RENTAL_APPLICATION_FEE_CATEGORIES.WAIVED]: 'Waived',
  [RENTAL_APPLICATION_FEE_CATEGORIES.PARTIAL]: 'Partially paid',
  [RENTAL_APPLICATION_FEE_CATEGORIES.IN_PROGRESS]: 'Payment in progress',
  [RENTAL_APPLICATION_FEE_CATEGORIES.RETRYABLE]: 'Payment needed'
}

const CATEGORY_COLORS: Record<RentalApplicationFeeCategory, string> = {
  [RENTAL_APPLICATION_FEE_CATEGORIES.PAID]: 'green',
  [RENTAL_APPLICATION_FEE_CATEGORIES.WAIVED]: 'purple',
  [RENTAL_APPLICATION_FEE_CATEGORIES.PARTIAL]: 'orange',
  [RENTAL_APPLICATION_FEE_CATEGORIES.IN_PROGRESS]: 'yellow',
  [RENTAL_APPLICATION_FEE_CATEGORIES.RETRYABLE]: 'blue'
}

const SELECTION_LABELS: Record<RentalApplicationFeeSelection, string> = {
  [RENTAL_APPLICATION_FEE_SELECTIONS.EFFECTIVE]: 'Active',
  [RENTAL_APPLICATION_FEE_SELECTIONS.SUPERSEDED]: 'Inactive',
  [RENTAL_APPLICATION_FEE_SELECTIONS.EXPIRED]: 'Expired'
}

const SELECTION_COLORS: Record<RentalApplicationFeeSelection, string> = {
  [RENTAL_APPLICATION_FEE_SELECTIONS.EFFECTIVE]: 'green',
  [RENTAL_APPLICATION_FEE_SELECTIONS.SUPERSEDED]: 'gray',
  [RENTAL_APPLICATION_FEE_SELECTIONS.EXPIRED]: 'orange'
}

const SELECTION_DISPLAY_PRIORITY: Record<RentalApplicationFeeSelection, number> = {
  [RENTAL_APPLICATION_FEE_SELECTIONS.EFFECTIVE]: 0,
  [RENTAL_APPLICATION_FEE_SELECTIONS.SUPERSEDED]: 1,
  [RENTAL_APPLICATION_FEE_SELECTIONS.EXPIRED]: 2
}

const formatDateTime = (value: string): string => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : format(date, 'MMM d, yyyy, h:mm a')
}

const formatPaymentDate = (value?: string | null): string => {
  if (!value) {
    return '—'
  }

  const date = parseISO(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : format(date, 'MMM d, yyyy')
}

const formatAmount = (value: unknown): string => formatMoney(Number(value), 2, '$')

const STRIPE_STATUS_LABELS: Record<string, string> = {
  initiated: 'Started',
  processing: 'Processing',
  succeeded: 'Succeeded',
  canceled: 'Canceled'
}

const formatStripeStatus = (value?: string | null, paymentIntentId?: string | null): string => {
  if (value) {
    return STRIPE_STATUS_LABELS[value] ?? value
  }

  return paymentIntentId ? 'Awaiting payment' : 'Not started'
}

const resolveFees = (fees: ApplicationFee[], now: Date) => {
  const normalizedFees = fees.map((original) => ({
    original,
    id: original.id,
    expectedAmountInCents: Math.round(Number(original.expected_amount) * 100),
    amountPaidInCents: Math.round(Number(original.amount_paid) * 100),
    createdAt: original.created_at,
    stripePaymentIntentStatus: original.stripe_payment_intent_status ?? null
  }))

  return resolveRentalApplicationFees(normalizedFees, now)
    .fees.map(({ fee, category, selection }) => ({
      fee: fee.original,
      category,
      selection
    }))
    .sort((left, right) => {
      const selectionDifference =
        SELECTION_DISPLAY_PRIORITY[left.selection] - SELECTION_DISPLAY_PRIORITY[right.selection]

      if (selectionDifference !== 0) {
        return selectionDifference
      }

      const createdAtDifference =
        new Date(right.fee.created_at).getTime() - new Date(left.fee.created_at).getTime()

      if (createdAtDifference !== 0) {
        return createdAtDifference
      }

      return right.fee.id.localeCompare(left.fee.id)
    })
}

const ApplicationFeesBody: FunctionComponent<
  React.PropsWithChildren<{
    fees: ApplicationFee[]
    fetching: boolean
    errorMessage?: string
  }>
> = ({ fees, fetching, errorMessage }) => {
  const rows = useMemo(() => resolveFees(fees, new Date()), [fees])

  if (fetching && !fees.length) {
    return (
      <div className='p-6' aria-label='Loading application fees'>
        <HStack spacing={3}>
          <Spinner size='sm' />
          <Text>Loading application fees…</Text>
        </HStack>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className='p-6'>
        <SC.ErrorMessage>Unable to load application fees: {errorMessage}</SC.ErrorMessage>
      </div>
    )
  }

  if (!rows.length) {
    return <div className='p-6 text-sm text-gray-500'>No application fees found for this user.</div>
  }

  return (
    <>
      <Box bg='gray.50' borderBottom='1px solid' borderColor='gray.200' px={4} py={3}>
        <Text color='gray.700' fontSize='sm'>
          Active is the fee currently used for this user&apos;s application. Inactive fees are not
          used for the application. Expired fees are 30 days old or older. Payment status shows how
          much of each fee was paid.
        </Text>
      </Box>
      <TableContainer>
        <Table size='sm' variant='simple'>
          <Thead>
            <Tr>
              <Th isNumeric>Fee amount</Th>
              <Th isNumeric>Paid</Th>
              <Th isNumeric>Balance</Th>
              <Th>Payment status</Th>
              <Th>Fee status</Th>
              <Th>Stripe status</Th>
              <Th>Payment date</Th>
              <Th>Created</Th>
              <Th>Updated</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map(({ fee, category, selection }, rowIndex) => {
              const balance = Math.max(Number(fee.expected_amount) - Number(fee.amount_paid), 0)

              return (
                <Tr key={fee.id}>
                  <Td isNumeric>{formatAmount(fee.expected_amount)}</Td>
                  <Td isNumeric>{formatAmount(fee.amount_paid)}</Td>
                  <Td isNumeric>{formatAmount(balance)}</Td>
                  <Td>
                    <Badge colorScheme={CATEGORY_COLORS[category]} textTransform='none'>
                      {CATEGORY_LABELS[category]}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={SELECTION_COLORS[selection]} textTransform='none'>
                      {SELECTION_LABELS[selection]}
                    </Badge>
                  </Td>
                  <Td>
                    <VStack align='start' spacing={1}>
                      <Text>
                        {formatStripeStatus(
                          fee.stripe_payment_intent_status,
                          fee.stripe_payment_intent_id
                        )}
                      </Text>
                      {fee.stripe_payment_intent_id && (
                        <Link
                          href={`https://dashboard.stripe.com/payments/${fee.stripe_payment_intent_id}`}
                          isExternal
                          showIcon
                        >
                          View in Stripe
                        </Link>
                      )}
                    </VStack>
                  </Td>
                  <Td whiteSpace='nowrap'>{formatPaymentDate(fee.payment_date)}</Td>
                  <Td whiteSpace='nowrap'>{formatDateTime(fee.created_at)}</Td>
                  <Td whiteSpace='nowrap'>{formatDateTime(fee.updated_at)}</Td>
                  <Td>
                    <Popover isLazy placement='left'>
                      <PopoverTrigger>
                        <Button
                          aria-label={`View details for fee ${
                            rowIndex + 1
                          }, created ${formatDateTime(fee.created_at)}`}
                          size='xs'
                          variant='outline'
                        >
                          Details
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent width='auto'>
                        <PopoverArrow />
                        <PopoverBody>
                          <Text color='gray.600' fontSize='xs' mb={1}>
                            Internal fee ID
                          </Text>
                          <HStack spacing={1}>
                            <Text fontFamily='mono' fontSize='xs'>
                              {fee.id}
                            </Text>
                            <CopyIcon textToCopy={fee.id} />
                          </HStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  )
}

const ApplicationFees: FunctionComponent<React.PropsWithChildren<Props>> = ({ userId }) => {
  const [{ data, fetching, error }] = useRentalApplicationFeesByUserIdQuery({
    variables: { userId },
    requestPolicy: REQUEST_POLICY.CACHE_AND_NETWORK
  })
  const fees = data?.rental_application_fees ?? []

  return (
    <ContentSectionCard
      title={`Application Fees (${fees.length})`}
      size='fit-content'
      isOpenDefault={false}
    >
      <ApplicationFeesBody fees={fees} fetching={fetching} errorMessage={error?.message} />
    </ContentSectionCard>
  )
}

export default ApplicationFees
